import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Check, Loader2, Search, X } from 'lucide-react';
import { cn, debounce } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import SearchSelect from '@/components/ui/search-select';
import { selectCurrentUser } from '@/redux/auth/auth.slice';
import { useGetProfileFeedQuery } from '@/redux/post/post.api.slice';
import { useGetCategoriesQuery } from '@/redux/category/category.api.slice';
import { ICategoryType } from '@/redux/category/category.d';
import { IPostVisibility } from '@/redux/post/post.d';
import { IFeedItem, IFeedItemPost } from '@/redux/feed/feed.d';
import { HINT } from './fileCardStyles';

const PAGE_SIZE = 24;
const SEARCH_DELAY_MS = 400;

/** Single workpieces only, and never one that already belongs to ANOTHER group —
 * the backend would reject the second claim. The group being edited is exempt,
 * or its own members would vanish from their own picker. Drafts and rejected
 * work can't be bundled either. */
// fallow-ignore-next-line complexity
function bundleable(
  item: IFeedItem,
  currentGroupId?: string
): item is IFeedItemPost {
  if (item.type !== 'post') return false;
  const claimedElsewhere =
    Boolean(item.projectGroupId) && item.projectGroupId !== currentGroupId;
  if (claimedElsewhere) return false;
  return (
    item.visibility === IPostVisibility.PUBLIC ||
    item.visibility === IPostVisibility.PRIVATE
  );
}

/** The same card the rail's Live Preview draws — image, title, byline, one pill
 * — so a project reads here exactly as the group being built does. Built out
 * rather than borrowing PostCard: this is a picker, not a feed. */
// fallow-ignore-next-line complexity
function ProjectTile({
  post,
  picked,
  onToggle,
}: {
  post: IFeedItemPost;
  picked: boolean;
  onToggle: () => void;
}) {
  const pill = post.difficulty;
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={picked}
      aria-label={post.title}
      onClick={onToggle}
      className={cn(
        'relative w-full overflow-hidden rounded-lg border bg-card text-left transition-shadow duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        picked
          ? 'border-primary shadow-[0_0_0_1px] shadow-primary'
          : 'border-border-strong hover:shadow-md'
      )}
    >
      <div className="px-3.5 pt-3">
        <div className="h-[132px] overflow-hidden rounded-lg bg-muted/50">
          {post.postThumbnail && (
            <img
              src={post.postThumbnail}
              alt={post.title}
              loading="lazy"
              className="h-full w-full object-cover"
            />
          )}
        </div>
      </div>

      <div className="p-3.5">
        <div title={post.title} className="truncate text-base font-semibold">
          {post.title}
        </div>
        <div className="mt-1 flex items-center gap-2">
          <span className="truncate text-xs text-muted-foreground">
            by{' '}
            <span className="font-medium text-foreground">
              {post.user?.username}
            </span>
          </span>
          <span className="grow" />
          {pill && (
            <span className="inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
              {pill}
            </span>
          )}
        </div>
      </div>

      {picked && (
        <Check className="absolute right-2.5 top-2.5 h-6 w-6 rounded-full bg-primary p-1 text-primary-foreground shadow-md" />
      )}
    </button>
  );
}

/** A project the picker can name in its tray without having it on screen. */
export interface PickedProject {
  _id: string;
  title: string;
}

/** The user's projects, picked into the group. Searched, filtered and paged on
 * the server — a prolific account has hundreds of projects, and none of the
 * pages it doesn't ask for are ever fetched. */
// fallow-ignore-next-line complexity
export default function ProjectPicker({
  selectedIds,
  onToggle,
  currentGroupId,
  initialSelected,
}: {
  selectedIds: string[];
  onToggle: (id: string) => void;
  /** Editing this group: its own members stay selectable. */
  currentGroupId?: string;
  /** Titles for members that no search result carries, so the tray can name
   * them on first paint. */
  initialSelected?: PickedProject[];
}) {
  const { user } = useSelector(selectCurrentUser);

  const [text, setText] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  // Every page merges into one cached list, so a narrowed search has to drop
  // the cursor or the first page it returns starts halfway down the old set.
  const [pageKey, setPageKey] = useState('');

  const { data: categories } = useGetCategoriesQuery({
    type: ICategoryType.POST,
  });
  const categoryId = useMemo(
    () => categories?.find((c) => c.name === category)?._id,
    [categories, category]
  );

  const debouncedSearch = useMemo(
    () => debounce((value: string) => setSearch(value.trim()), SEARCH_DELAY_MS),
    []
  );
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const key = `${search}|${categoryId ?? ''}`;
  if (key !== pageKey) {
    setPageKey(key);
    setCursor(undefined);
  }

  const { data, isLoading, isFetching } = useGetProfileFeedQuery(
    {
      username: user?.username ?? '',
      cursor,
      limit: PAGE_SIZE,
      search: search || undefined,
      category: categoryId,
    },
    { skip: !user?.username }
  );

  const items = (data?.items ?? []).filter((item): item is IFeedItemPost =>
    bundleable(item, currentGroupId)
  );

  // Every project the picker has ever seen, so the tray can still name one the
  // current search has scrolled out of the results.
  const titles = useRef(new Map<string, string>());
  (initialSelected ?? []).forEach((p) => titles.current.set(p._id, p.title));
  items.forEach((post) => titles.current.set(post._id, post.title));
  const picked = selectedIds.map((id) => ({
    _id: id,
    title: titles.current.get(id) ?? 'Untitled project',
  }));

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              debouncedSearch(e.target.value);
            }}
            placeholder="Search your projects by title…"
            aria-label="Search your projects"
            className="w-full rounded-full pl-9 pr-9"
          />
          {text && (
            <button
              type="button"
              onClick={() => {
                debouncedSearch.cancel();
                setText('');
                setSearch('');
              }}
              aria-label="Clear search"
              className="absolute right-2.5 top-1/2 flex -translate-y-1/2 items-center"
            >
              <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
            </button>
          )}
        </div>
        <div className="w-full sm:w-[220px]">
          <SearchSelect
            aria-label="Filter by category"
            value={category}
            options={(categories ?? []).map((c) => c.name)}
            onChange={setCategory}
            placeholder="Any category"
          />
        </div>
      </div>

      {/* The picked set, so it stays visible when a search hides its tiles. */}
      {picked.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {picked.map((post) => (
            <Badge
              key={post._id}
              variant="secondary"
              className="gap-1.5 rounded-full py-1 pl-3 pr-1.5"
            >
              <span className="max-w-[220px] truncate">{post.title}</span>
              <button
                type="button"
                onClick={() => onToggle(post._id)}
                aria-label={`Remove ${post.title}`}
                className="rounded-full p-0.5 hover:bg-background"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {['a', 'b', 'c', 'd'].map((k) => (
            <Skeleton key={k} className="h-[168px] w-full rounded-xl" />
          ))}
        </div>
      ) : (
        <>
          {items.length === 0 ? (
            <p className="rounded-xl border border-dashed py-10 text-center text-sm text-muted-foreground">
              {search || category
                ? 'No projects match that search.'
                : 'Nothing to bundle yet — publish a couple of single workpieces first.'}
            </p>
          ) : (
            <div
              role="group"
              aria-label="Your projects"
              // Fixed 280px, not a stretching minmax: the rail's preview card is
              // this wide, and these are meant to read as the same card.
              className="custom-scrollbar grid max-h-[560px] grid-cols-[repeat(auto-fill,280px)] gap-4 overflow-y-auto p-1"
            >
              {items.map((post) => (
                <ProjectTile
                  key={post._id}
                  post={post}
                  picked={selectedIds.includes(post._id)}
                  onToggle={() => onToggle(post._id)}
                />
              ))}
            </div>
          )}

          {data?.hasMore && data.nextCursor && (
            <Button
              variant="outline"
              size="sm"
              disabled={isFetching}
              onClick={() => setCursor(data.nextCursor ?? undefined)}
              className="gap-2"
            >
              {isFetching && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Load more projects
            </Button>
          )}
        </>
      )}

      <p className={HINT}>
        Projects already in another group aren&apos;t listed.
      </p>
    </div>
  );
}
